// Customer API Service Layer

import { 
  Customer, 
  CreateCustomerRequest, 
  UpdateCustomerRequest, 
  CustomerFilters,
  CustomerListResponse,
  CustomerResponse,
  CustomerSearchResponse,
  CustomerSearchResult
} from '@/types/customers'
import { createAuthenticatedApi, extractApiErrorMessage } from '@/lib/api'

const api = createAuthenticatedApi()

const normalizeCustomer = (customer: any): Customer => ({
  ...customer,
  branchName: customer.branch?.branchName || customer.branchName,
  branchCode: customer.branch?.branchCode || customer.branchCode,
})

const appendCustomerFormData = (data: CreateCustomerRequest | UpdateCustomerRequest) => {
  const formData = new FormData()

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return
    }

    formData.append(key, String(value))
  })

  return formData
}

// Get all customers with pagination and filtering
export const getCustomers = async (filters?: CustomerFilters): Promise<Customer[]> => {
  try {
    const params = new URLSearchParams()
    
    if (filters?.search) params.append('search', filters.search)
    if (filters?.city) params.append('city', filters.city)
    if (filters?.country) params.append('country', filters.country)
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString())
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())

    const response = await api.get<CustomerListResponse>(`/customers?${params.toString()}`)
    return response.data.data.map(normalizeCustomer)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error))
  }
}

// Get customer by ID
export const getCustomerById = async (id: string): Promise<Customer> => {
  try {
    const response = await api.get<CustomerResponse>(`/customers/${id}`)
    return normalizeCustomer(response.data.data)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error))
  }
}

// Create new customer
export const createCustomer = async (data: CreateCustomerRequest, pictureFile?: File | null): Promise<Customer> => {
  try {
    const formData = appendCustomerFormData(data)

    if (pictureFile) {
      formData.append('picture', pictureFile)
    }

    const response = await api.post<CustomerResponse>('/customers', formData)
    return normalizeCustomer(response.data.data)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error))
  }
}

// Update customer
export const updateCustomer = async (id: string, data: UpdateCustomerRequest, pictureFile?: File | null): Promise<Customer> => {
  try {
    const formData = appendCustomerFormData(data)

    if (pictureFile) {
      formData.append('picture', pictureFile)
    }

    const response = await api.put<CustomerResponse>(`/customers/${id}`, formData)
    return normalizeCustomer(response.data.data)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error))
  }
}

// Archive customer
export const deleteCustomer = async (id: string): Promise<void> => {
  try {
    await api.delete(`/customers/${id}`)
  } catch (error) {
    throw new Error(extractApiErrorMessage(error))
  }
}

// Search customers
export const searchCustomers = async (query: string, limit: number = 10): Promise<CustomerSearchResult[]> => {
  try {
    const response = await api.get<CustomerSearchResponse>(`/customers/search?q=${encodeURIComponent(query)}&limit=${limit}`)
    return response.data.data
  } catch (error) {
    throw new Error(extractApiErrorMessage(error))
  }
}

// Get customers by city
export const getCustomersByCity = async (city: string): Promise<Customer[]> => {
  try {
    const response = await api.get<CustomerListResponse>(`/customers?city=${encodeURIComponent(city)}`)
    return response.data.data
  } catch (error) {
    throw new Error(extractApiErrorMessage(error))
  }
}

// Get customers by country
export const getCustomersByCountry = async (country: string): Promise<Customer[]> => {
  try {
    const response = await api.get<CustomerListResponse>(`/customers?country=${encodeURIComponent(country)}`)
    return response.data.data
  } catch (error) {
    throw new Error(extractApiErrorMessage(error))
  }
}

// Get customer statistics (for future use)
export const getCustomerStatistics = async (): Promise<{
  totalCustomers: number
  activeCustomers: number
  inactiveCustomers: number
  newCustomersThisMonth: number
}> => {
  try {
    // This endpoint doesn't exist yet, but we can calculate from customer list
    const allCustomers = await getCustomers({ limit: 1000 })
    const now = new Date()
    const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    
    return {
      totalCustomers: allCustomers.length,
      activeCustomers: allCustomers.filter(c => c.isActive).length,
      inactiveCustomers: allCustomers.filter(c => !c.isActive).length,
      newCustomersThisMonth: allCustomers.filter(c => new Date(c.createdAt) >= thisMonth).length
    }
  } catch (error) {
    throw new Error(extractApiErrorMessage(error))
  }
}
